import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { plainConfirmMessage, usePreviewConfirm } from "@/lib/confirmFlow";
import { formatDay } from "@/lib/format";
import { useIsAdmin } from "@/lib/session";
import { useToast } from "@/components/Toaster";
import { Facts, Problem, ServerSays } from "@/components/PreviewBits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

/** billing_club_move's preview (internal/billing/keapclub.go previewClubMove). */
export interface ClubMovePreview {
  member?: string;
  plan?: string;
  first_crm_charge?: string;
  card?: string;
  keap_order_id?: number;
  keap_auto_charge_now?: boolean;
  steps?: string[];
}

interface ClubMoveOut {
  state: string;
  message: string;
}

export interface MovableSub {
  id: number;
  plan: string;
  status: string;
  billed_by?: string;
  ends_on?: string | null;
}

function refresh(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ["billing-detail", "billing-summary", "subscriptions", "subscription", "club-cards"]) void qc.invalidateQueries({ queryKey: [k] });
}

/** The contact page's Club move controls: "Move Club billing to the CRM" on a Keap-billed membership,
 *  and "Undo the move" while it waits for Keap to show billing off. Admins only (it moves money). */
export function ClubMoveControls({ sub }: { sub: MovableSub }) {
  const isAdmin = useIsAdmin();
  const [open, setOpen] = useState<"move" | "undo" | null>(null);
  const active = ["active", "trialing"].includes(sub.status) && !sub.ends_on;
  if (sub.billed_by === "moving")
    return (
      <>
        <Badge variant="warning" title="The CRM claimed it; it takes over once Keap shows this member's billing off">
          moving: waiting for Keap off
        </Badge>
        {isAdmin && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setOpen("undo")}>
            Undo the move
          </Button>
        )}
        {open === "undo" && <ClubMoveUndoDialog sub={sub} onClose={() => setOpen(null)} />}
      </>
    );
  if (sub.billed_by !== "keap" || !active || !isAdmin) return null;
  return (
    <>
      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => setOpen("move")}>
        Move Club billing to the CRM
      </Button>
      {open === "move" && <ClubMoveDialog sub={sub} onClose={() => setOpen(null)} />}
    </>
  );
}

/** billing_club_move (money): the server's preview first (Keap read live), then confirm. */
export function ClubMoveDialog({ sub, onClose }: { sub: MovableSub; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const flow = usePreviewConfirm<ClubMovePreview, ClubMoveOut>(
    (confirm) => api<ClubMoveOut>(`/api/v1/subscriptions/${sub.id}/move-from-keap`, { json: { confirm: confirm || undefined } }),
    (r) => {
      refresh(qc);
      toast(r.message || "Move started");
      onClose();
    },
  );
  const p = flow.preview?.details;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(600px,calc(100vw-2rem))]">
        <DialogTitle>{p ? "Check before moving" : `Move ${sub.plan} billing to the CRM`}</DialogTitle>
        {!p ? (
          <DialogDescription>
            The CRM takes over this member's Club renewals on the card saved in Stripe. First it reads Keap live and shows you what Keap stops, what the CRM starts and when.
            Nothing changes until you confirm on the next step, and nothing is ever written to Keap.
          </DialogDescription>
        ) : (
          <div className="space-y-3">
            <ServerSays text={plainConfirmMessage(flow.preview!.message)} />
            <Facts
              rows={[
                ["Member", p.member],
                ["Plan", p.plan],
                ["Card on file", p.card || "none"],
                ["First CRM charge", p.first_crm_charge ? formatDay(p.first_crm_charge) : undefined],
                ["Keap recurring order", p.keap_order_id ? `#${p.keap_order_id}` : undefined],
                ["Keap auto-charge now", p.keap_auto_charge_now === undefined ? undefined : p.keap_auto_charge_now ? "on (you turn it off in Keap next)" : "already off"],
              ]}
            />
            {p.steps && p.steps.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium">What happens</p>
                <ol className="list-decimal space-y-1 pl-5 text-sm">
                  {p.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
        <Problem text={flow.problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={p ? flow.back : onClose}>
            {p ? "Back" : "Cancel"}
          </Button>
          {!p ? (
            <Button disabled={flow.reviewing} onClick={flow.review}>
              {flow.reviewing ? "Asking Keap…" : "Show what happens"}
            </Button>
          ) : (
            <Button variant="destructive" disabled={flow.confirming} onClick={flow.confirm}>
              Start the move
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** billing_club_move_undo: hands a moving membership back to Keap (asks first). */
export function ClubMoveUndoDialog({ sub, onClose }: { sub: MovableSub; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const flow = usePreviewConfirm<{ keap_order_id?: number }>(
    (confirm) => api(`/api/v1/subscriptions/${sub.id}/move-from-keap/undo`, { json: { confirm: confirm || undefined } }),
    () => {
      refresh(qc);
      toast("Handed back to Keap");
      onClose();
    },
  );
  const p = flow.preview;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>Undo the move of {sub.plan}?</DialogTitle>
        {!p ? (
          <DialogDescription>Keap keeps billing this member and the CRM stops. Nothing is changed in Keap: turn auto-charge back on there yourself if it is off.</DialogDescription>
        ) : (
          <div className="space-y-2">
            <ServerSays text={plainConfirmMessage(p.message)} />
            <Facts rows={[["Keap recurring order", p.details.keap_order_id ? `#${p.details.keap_order_id}` : undefined]]} />
          </div>
        )}
        <Problem text={flow.problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Keep the move
          </Button>
          {!p ? (
            <Button disabled={flow.reviewing} onClick={flow.review}>
              Review
            </Button>
          ) : (
            <Button variant="destructive" disabled={flow.confirming} onClick={flow.confirm}>
              Hand back to Keap
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
